import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../lib-page-header.css';
import './IssueManagementIndex.css';
import { useSectionBase } from '../../Constant/useActiveLibrary';

import IssuedBookList from './IssuedBookList';
import RenewalList from '../Renewals/RenewalList';
import PaymentList from '../Payments/PaymentList';
import DepositLedger from '../Payments/DepositLedger';

const IssueManagementIndex = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sectionBase = useSectionBase();
  const [activeTab, setActiveTab] = useState('issued');
  const [actionsNode, setActionsNode] = useState(null);

  // This page must always open (or be revisited from the sidebar) scrolled
  // to the very top — the browser otherwise keeps whatever scrollY the
  // previous page left behind, which reads as "it loads halfway down the
  // form". location.key changes on every navigation (even re-clicking the
  // same sidebar link), so this fires reliably on open AND on revisit.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.key]);

  const tabs = [
    {
      key: 'issued',
      label: 'Issued Books',
      icon: 'ti ti-books',
      pageTitle: 'Issue Book',
      pageSubtitle: 'Issue book and check member fine details on the same page'
    },
    {
      key: 'renewals',
      label: 'Renewals',
      icon: 'ti ti-refresh',
      pageTitle: 'Book Renewals',
      pageSubtitle: 'Renew issued books and extend due date'
    },
    {
      key: 'payments',
      label: 'Payments',
      icon: 'ti ti-cash',
      pageTitle: 'Payments',
      pageSubtitle: 'Collect fine, deposit or membership payments'
    },
    {
      key: 'deposit',
      label: 'Deposit Ledger',
      icon: 'ti ti-wallet',
      pageTitle: 'Deposit Ledger',
      pageSubtitle: 'View membership/security deposit ledger'
    }
  ];

  const activeTabMeta = tabs.find((tab) => tab.key === activeTab);

  const renderContent = () => {
    if (activeTab === 'issued') return <IssuedBookList />;
    if (activeTab === 'renewals') return <RenewalList embedded actionsPortalNode={actionsNode} />;
    if (activeTab === 'payments') return <PaymentList embedded actionsPortalNode={actionsNode} />;
    if (activeTab === 'deposit') return <DepositLedger embedded />;

    return <IssuedBookList />;
  };

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header im-merged-header">
        <div className="lib-page-header__left im-merged-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-book-reader" />
          </div>
          <div className="im-header-block">
            <h3 className="lib-page-header__title">Issued Book Management</h3>
            <div className="lib-page-header__subtitle">Manage issued books, renewals, fine payments and deposit ledger</div>
          </div>

          {activeTabMeta && (
            <>
              <div className="im-header-divider" />
              <div className="im-header-block">
                <h3 className="lib-page-header__title im-header-title--sub">{activeTabMeta.pageTitle}</h3>
                <div className="lib-page-header__subtitle">{activeTabMeta.pageSubtitle}</div>
              </div>
            </>
          )}
        </div>

        <div className="lib-page-header__actions" ref={setActionsNode}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`im-tab-btn ${activeTab === tab.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <i className={tab.icon} />
              {tab.label}
            </button>
          ))}

          {/* Shortcuts into the centralized Reports module (per the Reports
              module requirements) — both open the exact same reports
              reachable from Reports > Circulation, never a second
              implementation of issue history. */}
          <button
            type="button"
            className="im-tab-btn"
            title="Open the centralized Member Issue History report"
            onClick={() => navigate(`${sectionBase}/reports/member-issue-history`)}
          >
            <i className="fas fa-history" /> Member History
          </button>
          <button
            type="button"
            className="im-tab-btn"
            title="Open the centralized Book Issue History report"
            onClick={() => navigate(`${sectionBase}/reports/book-issue-history`)}
          >
            <i className="fas fa-book" /> Book History
          </button>

          {activeTab === 'issued' && (
            <button
              type="button"
              className="im-back-btn"
              onClick={() => navigate(`${sectionBase}/issues`)}
            >
              <i className="ti ti-arrow-left" /> Back
            </button>
          )}
        </div>
      </div>

      <div>{renderContent()}</div>
    </div>
  );
};

export default IssueManagementIndex;